import { figmaPxToWidthPercent } from '@ui/Report';

import { REPORT_BODY_TEXT } from '../EditablePageHeader/editable-page-header.constants';

/** Figma text color for product groups report page. */
export const PRODUCT_GROUPS_TEXT_COLOR = '#282a31';

/** Leader-line stroke color — same as text color, opacity applied via strokeOpacity. */
export const PRODUCT_GROUPS_LEADER_LINE_COLOR = PRODUCT_GROUPS_TEXT_COLOR;

/** SBEF legend row labels — ~1% of report page width (scaling number). */
export const SBEF_LEGEND_TEXT_FONT_SIZE = 1;

/** Section headings "Elements" / "Products" — 50% larger than legend labels. */
export const REPORT_SECTION_HEADING = {
  fontSize: SBEF_LEGEND_TEXT_FONT_SIZE * 1.5,
  fontWeight: 900,
  letterSpacing: figmaPxToWidthPercent(0.8),
  lineHeight: 1.2,
} as const;

/** Chart label name — Roboto Bold 11px, lh 1.3, opacity 80%. */
export const PRODUCT_GROUPS_LABEL_NAME = {
  fontSize: figmaPxToWidthPercent(11),
  fontWeight: 700,
  lineHeight: 1.3,
  opacity: 0.8,
} as const;

/** Cloud icon + primary value row — icon 22px, readable next to the value. */
export const PRODUCT_GROUPS_LABEL_VALUE = {
  fontSize: figmaPxToWidthPercent(11),
  lineHeight: 1.3,
  opacity: 0.8,
  cloudFontWeight: 900,
  arrowFontWeight: 400,
  iconSize: figmaPxToWidthPercent(22),
  iconOpacity: 0.75,
  chipSize: figmaPxToWidthPercent(12),
  chipOpacity: 0.8,
  groupGap: figmaPxToWidthPercent(8),
  innerGap: figmaPxToWidthPercent(4),
} as const;

/**
 * Sole donut-center label in SVG viewBox units (0–100).
 *
 * Derived from the Figma inner-hole ratio: fontPx / innerHolePx × innerDiameterVB.
 * The old caption was 11.333px, weight 400, opacity 0.45 under a larger level
 * number. That number is gone, so this line uses the number's weight and full
 * opacity at 16px — readable, and still inside the hole for "100% reduction".
 */
const CENTER_LABEL_FONT_PX = 16;
const INNER_HOLE_PX = 171.369;
const INNER_DIAMETER_VB = (24 / 46) * 50 * 2;
export const PRODUCT_GROUPS_CENTER_TEXT = {
  reductionFontSizeVb:
    (CENTER_LABEL_FONT_PX / INNER_HOLE_PX) * INNER_DIAMETER_VB,
  reductionFontWeight: 700,
  reductionOpacity: 1,
} as const;

/** Top-right proposal total + offset row — same scale as {@link OffsetBox} on this page. */
const PROPOSAL_SUMMARY_OFFSET_FONT_SIZE = figmaPxToWidthPercent(62 * 0.75);

export const PRODUCT_GROUPS_PROPOSAL_SUMMARY = {
  rowGap: figmaPxToWidthPercent(24),
  /** Offset box value — Figma 62px, scaled down ~25%. */
  offsetFontSize: PROPOSAL_SUMMARY_OFFSET_FONT_SIZE,
  /** Proposal total next to the cloud — ~50% larger than {@link offsetFontSize}. */
  totalFontSize: PROPOSAL_SUMMARY_OFFSET_FONT_SIZE * 1.5,
  /** Cloud icon — large enough to read next to the proposal total. */
  cloudIconSize: figmaPxToWidthPercent(52),
  co2eInnerGap: figmaPxToWidthPercent(9),
  /** Matches {@link REPORT_BODY_TEXT} — page description under the title. */
  caption: REPORT_BODY_TEXT,
  captionOpacity: 0.6,
  /** Desired visual gap between values and captions. */
  captionGap: figmaPxToWidthPercent(16),
} as const;

/** Used by label layout estimation — mirrors {@link PRODUCT_GROUPS_LABEL_NAME} and values row. */
export const PRODUCT_GROUPS_LABEL_LAYOUT = {
  nameFontSize: PRODUCT_GROUPS_LABEL_NAME.fontSize,
  nameLineHeight: PRODUCT_GROUPS_LABEL_NAME.lineHeight,
  valuesRowHeight:
    PRODUCT_GROUPS_LABEL_VALUE.fontSize * PRODUCT_GROUPS_LABEL_VALUE.lineHeight,
  rowGap: figmaPxToWidthPercent(2),
  padding: 0,
  maxWidth: figmaPxToWidthPercent(167),
  charWidthRatio: 0.58,
  valuesCloudGroup:
    PRODUCT_GROUPS_LABEL_VALUE.iconSize +
    PRODUCT_GROUPS_LABEL_VALUE.innerGap +
    figmaPxToWidthPercent(28),
  valuesArrowGroup:
    PRODUCT_GROUPS_LABEL_VALUE.chipSize +
    PRODUCT_GROUPS_LABEL_VALUE.innerGap +
    figmaPxToWidthPercent(25),
  valuesBetweenGap: PRODUCT_GROUPS_LABEL_VALUE.groupGap,
} as const;
